import React, { useEffect, useMemo, useState } from "react";
import { useMoney } from "../../hooks/useMoney";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Bell,
  CheckCircle,
  Mail,
  MessageSquare,
  Wallet,
  TrendingDown,
  X,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { RootState } from "../../store/store";
import {
  clearNotificationConfig,
  clearNotificationConfigSaveStatus,
  fetchNotificationConfig,
  getDistributorById,
  NotificationAlertContact,
  upsertNotificationConfig,
} from "../../features/distributor/DistributorSlice";

type DistributorRef = {
  distributorId: number;
  name: string;
};

interface DistributorNotificationConfigPageProps {
  distributor: DistributorRef;
  initialProgramId?: number;
  onBack: () => void;
}

type ContactDraft = {
  channel: "EMAIL" | "SMS";
  contactValue: string;
  isActive: boolean;
};

const emptyContact = (): ContactDraft => ({
  channel: "EMAIL",
  contactValue: "",
  isActive: true,
});

const DistributorNotificationConfigPage: React.FC<DistributorNotificationConfigPageProps> = ({
  distributor,
  initialProgramId,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const { symbol, code, locale } = useMoney();
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(value);
  };
  const {
    selectedDistributor,
    loading: distributorLoading,
    error: distributorError,
    notificationConfig,
    notificationConfigLoading,
    notificationConfigError,
    notificationConfigSaving,
    notificationConfigSaveError,
    notificationConfigSaveSuccess,
  } = useAppSelector((state: RootState) => state.distributor);

  const [selectedProgramId, setSelectedProgramId] = useState<string>(
    initialProgramId ? String(initialProgramId) : ""
  );
  const [thresholdBalance, setThresholdBalance] = useState<string>("");
  const [contacts, setContacts] = useState<ContactDraft[]>([emptyContact()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  const walletPrograms = useMemo(
    () =>
      selectedDistributor?.wallet?.filter(
        (p) => p.ProgramDistributorWallet?.isActive !== false
      ) ?? [],
    [selectedDistributor?.wallet]
  );

  const selectedProgram = walletPrograms.find(
    (p) => String(p.programId) === selectedProgramId
  );

  const parsedThreshold =
    thresholdBalance.trim() === "" ? null : Number(thresholdBalance);

  const thresholdStatus = useMemo(() => {
    if (parsedThreshold === null || Number.isNaN(parsedThreshold)) {
      return { label: "Alerts off", tone: "neutral" as const };
    }
    const balance = notificationConfig?.cumulativeBalance ?? 0;
    if (balance < parsedThreshold) {
      return { label: "Below threshold", tone: "warning" as const };
    }
    return { label: "Above threshold", tone: "ok" as const };
  }, [parsedThreshold, notificationConfig?.cumulativeBalance]);

  useEffect(() => {
    dispatch(getDistributorById(distributor.distributorId));
    return () => {
      dispatch(clearNotificationConfig());
    };
  }, [dispatch, distributor.distributorId]);

  useEffect(() => {
    if (!selectedProgramId && walletPrograms.length > 0) {
      const defaultId = initialProgramId
        ? String(initialProgramId)
        : String(walletPrograms[0].programId);
      setSelectedProgramId(defaultId);
    }
  }, [walletPrograms, selectedProgramId, initialProgramId]);

  useEffect(() => {
    if (!selectedProgramId) return;
    dispatch(
      fetchNotificationConfig({
        distributorId: distributor.distributorId,
        programId: Number(selectedProgramId),
      })
    );
  }, [dispatch, distributor.distributorId, selectedProgramId]);

  useEffect(() => {
    if (!notificationConfig) return;
    setThresholdBalance(
      notificationConfig.thresholdBalance !== null &&
        notificationConfig.thresholdBalance !== undefined
        ? String(notificationConfig.thresholdBalance)
        : ""
    );
    setContacts(
      notificationConfig.contacts?.length
        ? notificationConfig.contacts.map((c: NotificationAlertContact) => ({
            channel: c.channel,
            contactValue: c.contactValue,
            isActive: c.isActive !== false,
          }))
        : [emptyContact()]
    );
  }, [notificationConfig]);

  useEffect(() => {
    if (notificationConfigSaveSuccess) {
      setShowSuccessBanner(true);
    }
  }, [notificationConfigSaveSuccess]);

  const validateContacts = (): boolean => {
    const filled = contacts.filter((c) => c.contactValue.trim() !== "");
    for (const contact of filled) {
      if (contact.channel === "EMAIL") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.contactValue.trim())) {
          setFormError("Enter a valid email for each email contact.");
          return false;
        }
      } else if (!/^\+?[0-9]{10,15}$/.test(contact.contactValue.trim())) {
        setFormError("Phone numbers must be 10–15 digits (optional + prefix).");
        return false;
      }
    }
    setFormError(null);
    return true;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearNotificationConfigSaveStatus());
    setShowSuccessBanner(false);

    if (!selectedProgramId) {
      setFormError("Select a program to configure.");
      return;
    }
    if (!validateContacts()) return;

    if (
      thresholdBalance.trim() !== "" &&
      (Number.isNaN(parsedThreshold) || (parsedThreshold as number) < 0)
    ) {
      setFormError("Threshold must be empty or a non-negative amount.");
      return;
    }

    const payloadContacts = contacts
      .filter((c) => c.contactValue.trim() !== "")
      .map((c) => ({
        channel: c.channel,
        contactValue: c.contactValue.trim(),
        isActive: c.isActive,
      }));

    await dispatch(
      upsertNotificationConfig({
        distributorId: distributor.distributorId,
        programId: Number(selectedProgramId),
        thresholdBalance: parsedThreshold,
        contacts: payloadContacts,
      })
    );
  };

  const handleContactChange = (
    index: number,
    field: keyof ContactDraft,
    value: string | boolean
  ) => {
    setContacts((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleAddContact = () => {
    setContacts((prev) => [...prev, emptyContact()]);
  };

  const handleRemoveContact = (index: number) => {
    setContacts((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : [emptyContact()]
    );
  };

  const isLoading = distributorLoading || notificationConfigLoading;
  const alertsEnabled =
    parsedThreshold !== null && !Number.isNaN(parsedThreshold);

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-cardborder">
        <div className="max-w-7xl mx-auto px-7 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wallet alerts</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {distributor.name} · Low-balance notifications per program
              </p>
            </div>
          </div>
          {!isLoading && walletPrograms.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Bell className="w-4 h-4 text-brand shrink-0" />
              <span>
                {alertsEnabled
                  ? "Threshold monitoring enabled"
                  : "No threshold set for this program"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 py-8">
        {(distributorError || notificationConfigError) && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 text-red-800"
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm">{distributorError || notificationConfigError}</p>
          </div>
        )}

        {notificationConfigSaveError && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 text-red-800"
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm">{notificationConfigSaveError}</p>
          </div>
        )}

        {showSuccessBanner && (
          <div
            role="status"
            className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center justify-between gap-3 text-green-900"
          >
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-5 h-5 shrink-0 text-green-600" />
              Settings saved for this program wallet.
            </div>
            <button
              type="button"
              onClick={() => {
                setShowSuccessBanner(false);
                dispatch(clearNotificationConfigSaveStatus());
              }}
              className="p-1 rounded-md hover:bg-green-100 text-green-700"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {isLoading && (
          <div className="bg-white rounded-xl border border-cardborder p-12 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
            <p className="text-sm text-gray-500">Loading wallet settings…</p>
          </div>
        )}

        {!isLoading && walletPrograms.length === 0 && (
          <div className="bg-white rounded-xl border border-cardborder p-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Wallet className="w-6 h-6 text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No program wallets</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              Assign at least one program to this distributor before configuring balance
              alerts.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand/5"
            >
              Back to distributors
            </button>
          </div>
        )}

        {!isLoading && walletPrograms.length > 0 && (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white rounded-xl border border-cardborder p-6">
                  <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                    Program
                  </h2>
                  <label htmlFor="program-select" className="sr-only">
                    Program
                  </label>
                  <select
                    id="program-select"
                    value={selectedProgramId}
                    onChange={(e) => setSelectedProgramId(e.target.value)}
                    className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  >
                    {walletPrograms.map((prog) => (
                      <option key={prog.programId} value={prog.programId}>
                        {prog.programName}
                      </option>
                    ))}
                  </select>
                  {selectedProgram?.logo && (
                    <div className="mt-4 flex items-center gap-3 pt-4 border-t border-gray-100">
                      <img
                        src={selectedProgram.logo}
                        alt=""
                        className="h-8 w-12 object-contain rounded"
                      />
                      <span className="text-sm text-gray-600 truncate">
                        {selectedProgram.programName}
                      </span>
                    </div>
                  )}
                </div>

                {notificationConfig && (
                  <div className="bg-white rounded-xl border border-cardborder p-6 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Wallet snapshot
                    </h2>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="p-2 rounded-lg bg-white border border-gray-100">
                        <Wallet className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Current balance</p>
                        <p className="text-lg font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(notificationConfig.cumulativeBalance)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="p-2 rounded-lg bg-white border border-gray-100">
                        <TrendingDown className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">Alert threshold</p>
                        <p className="text-lg font-semibold text-gray-900 tabular-nums">
                          {alertsEnabled
                            ? formatCurrency(parsedThreshold)
                            : "Not set"}
                        </p>
                        <span
                          className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            thresholdStatus.tone === "warning"
                              ? "bg-amber-100 text-amber-800"
                              : thresholdStatus.tone === "ok"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {thresholdStatus.label}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl border border-cardborder p-6">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="p-2 rounded-lg bg-brand/10">
                      <TrendingDown className="w-5 h-5 text-brand" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Balance threshold
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Notify contacts when balance drops below this amount after an order.
                        Clear the field to turn off alerts for this program.
                      </p>
                    </div>
                  </div>
                  <label
                    htmlFor="threshold-balance"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum balance ({symbol})
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <input
                      id="threshold-balance"
                      type="number"
                      min={0}
                      step="0.01"
                      value={thresholdBalance}
                      onChange={(e) => setThresholdBalance(e.target.value)}
                      placeholder="Leave empty to disable"
                      className="block w-full sm:max-w-xs px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                    />
                    {thresholdBalance.trim() !== "" && (
                      <button
                        type="button"
                        onClick={() => setThresholdBalance("")}
                        className="text-sm text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline shrink-0"
                      >
                        Clear threshold
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-cardborder p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-brand/10">
                        <Bell className="w-5 h-5 text-brand" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Notification contacts
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          People who receive low-balance alerts for this program.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddContact}
                      className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand/90 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add recipient
                    </button>
                  </div>

                  <div className="space-y-3">
                    {contacts.map((contact, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50/80"
                      >
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">
                            Channel
                          </label>
                          <select
                            value={contact.channel}
                            onChange={(e) =>
                              handleContactChange(
                                index,
                                "channel",
                                e.target.value as "EMAIL" | "SMS"
                              )
                            }
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                          >
                            <option value="EMAIL">Email</option>
                            <option value="SMS">SMS</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">
                            {contact.channel === "EMAIL" ? "Email" : "Mobile"}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                              {contact.channel === "EMAIL" ? (
                                <Mail className="w-4 h-4" />
                              ) : (
                                <MessageSquare className="w-4 h-4" />
                              )}
                            </span>
                            <input
                              type={contact.channel === "EMAIL" ? "email" : "tel"}
                              value={contact.contactValue}
                              onChange={(e) =>
                                handleContactChange(index, "contactValue", e.target.value)
                              }
                              placeholder={
                                contact.channel === "EMAIL"
                                  ? "recipient@company.com"
                                  : "10-digit mobile"
                              }
                              autoComplete="off"
                              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                            />
                          </div>
                        </div>
                        <div className="flex md:flex-col items-center md:items-end justify-between md:justify-end gap-3 pt-1 md:pt-6">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={contact.isActive}
                              onChange={(e) =>
                                handleContactChange(index, "isActive", e.target.checked)
                              }
                              className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
                            />
                            Active
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(index)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            aria-label="Remove recipient"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {contacts.every((c) => !c.contactValue.trim()) && (
                    <p className="mt-4 text-sm text-gray-500 italic">
                      Add at least one email or SMS recipient to receive alerts.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {formError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-2 text-sm text-red-800"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                {formError}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-2 pb-8">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={notificationConfigSaving}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-brand hover:bg-brand/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {notificationConfigSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save settings"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DistributorNotificationConfigPage;
